import { buildFrontendMapAssetDocument, saveMapAssetDocument, type MapAssetDocument } from './mapAsset';
import { loadClientMapDefinition } from '../../terrain/MapLoader';
import type { GameState, MapDefinition } from '../../model/gamestate/GameState';
import type { TileMapRenderer } from '../../renderer/tilemap/TileMapRenderer';
import type { AuthoritativeMapDefinition, SpaceTimeClient } from '../../spacetime/SpaceTimeClient';

type WindowWithOpenFilePicker = Window & {
  showOpenFilePicker?: (options?: {
    multiple?: boolean;
    excludeAcceptAllOption?: boolean;
    types?: Array<{ description?: string; accept: Record<string, string[]> }>;
  }) => Promise<Array<{ getFile(): Promise<File> }>>;
};

interface RefreshMapOptions {
  refreshRenderer?: boolean;
  refreshNpcs?: boolean;
}

interface CreateAdminMapEditControllerParams {
  client: SpaceTimeClient;
  gameState: GameState;
  renderer: TileMapRenderer;
  mapCache: Map<string, MapDefinition>;
  refreshNpcs: (mapId: string, force?: boolean) => Promise<void>;
  waitForMyPlayerSnapshot?: SpaceTimeClient['players']['waitForMyPlayerSnapshot'];
  chooseJsonFile?: () => Promise<File | null>;
  loadMapDefinition?: typeof loadClientMapDefinition;
  buildMapAssetDocument?: (
    source: AuthoritativeMapDefinition,
    map: MapDefinition,
  ) => Promise<MapAssetDocument>;
  saveMapAssetDocument?: (document: MapAssetDocument) => Promise<string>;
}

export interface AdminMapEditController {
  runMutation<T>(operation: () => Promise<T>): Promise<T>;
  waitForIdle(): Promise<void>;
  refreshMap(mapId: string, options?: RefreshMapOptions): Promise<MapDefinition>;
  wipeAndReloadMaps(): Promise<void>;
  uploadMapJson(): Promise<boolean>;
  saveFrontendMapAsset(): Promise<string>;
  setMapDefaultBiome(mapId: string, biomeType: string): Promise<void>;
}

async function defaultChooseJsonFile(): Promise<File | null> {
  const openPicker = (window as WindowWithOpenFilePicker).showOpenFilePicker;
  if (openPicker) {
    try {
      const [handle] = await openPicker({
        multiple: false,
        excludeAcceptAllOption: false,
        types: [{ description: 'Map JSON', accept: { 'application/json': ['.json'] } }],
      });
      return handle ? await handle.getFile() : null;
    } catch {
      return null;
    }
  }

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'application/json,.json';
  return await new Promise<File | null>((resolve) => {
    let settled = false;
    const finish = (file: File | null): void => {
      if (settled) {
        return;
      }
      settled = true;
      window.removeEventListener('focus', handleFocus, true);
      resolve(file);
    };
    const handleFocus = (): void => {
      window.setTimeout(() => finish(input.files?.[0] ?? null), 0);
    };
    input.addEventListener('change', () => finish(input.files?.[0] ?? null), { once: true });
    window.addEventListener('focus', handleFocus, true);
    input.click();
  });
}

export function createAdminMapEditController(
  params: CreateAdminMapEditControllerParams,
): AdminMapEditController {
  const {
    client,
    gameState,
    renderer,
    mapCache,
    refreshNpcs,
    waitForMyPlayerSnapshot = client.players.waitForMyPlayerSnapshot.bind(client.players),
    chooseJsonFile = defaultChooseJsonFile,
    loadMapDefinition = loadClientMapDefinition,
    buildMapAssetDocument = buildFrontendMapAssetDocument,
    saveMapAssetDocument: persistMapAssetDocument = saveMapAssetDocument,
  } = params;

  let pendingMutation: Promise<void> = Promise.resolve();

  const enqueueOperation = <T>(operation: () => Promise<T>): Promise<T> => {
    const next = pendingMutation
      .catch(() => undefined)
      .then(operation);
    pendingMutation = next.then(() => undefined, () => undefined);
    return next;
  };

  const clearMapCache = (mapId?: string): void => {
    client.maps.clearMapDefinitionCache(mapId);
    if (typeof mapId === 'string') {
      mapCache.delete(mapId);
      return;
    }
    mapCache.clear();
  };

  const fetchAuthoritativeMapDefinition = async (mapId: string): Promise<AuthoritativeMapDefinition> => {
    clearMapCache(mapId);
    const definition = await client.maps.getMapDefinition(mapId, { forceRefresh: true });
    if (!definition) {
      throw new Error(`Map '${mapId}' is not cached on the client for export.`);
    }
    return definition;
  };

  const refreshMap = async (mapId: string, options: RefreshMapOptions = {}): Promise<MapDefinition> => {
    clearMapCache(mapId);
    const refreshedMap = await loadMapDefinition(client, mapCache, mapId);
    const shouldApply = options.refreshRenderer !== false || gameState.getCurrentMapId() === mapId;
    if (shouldApply) {
      gameState.setMap(refreshedMap, mapId);
      if (options.refreshRenderer !== false) {
        renderer.refreshCurrentMap(refreshedMap);
      }
      if (options.refreshNpcs === true) {
        await refreshNpcs(mapId, true);
      }
    }
    return refreshedMap;
  };

  return {
    runMutation<T>(operation: () => Promise<T>): Promise<T> {
      return enqueueOperation(operation);
    },

    async waitForIdle(): Promise<void> {
      await pendingMutation;
    },

    refreshMap,

    async wipeAndReloadMaps(): Promise<void> {
      await enqueueOperation(async () => {
        await client.admin.wipeAndReloadMaps();
        clearMapCache();
        const authoritativePlayer = await waitForMyPlayerSnapshot();
        const targetMapId = authoritativePlayer?.currentMapId ?? gameState.getCurrentMapId();
        await refreshMap(targetMapId, { refreshRenderer: true, refreshNpcs: true });
      });
    },

    async uploadMapJson(): Promise<boolean> {
      return enqueueOperation(async () => {
        const file = await chooseJsonFile();
        if (!file) {
          return false;
        }

        const rawJson = await file.text();
        const parsed = JSON.parse(rawJson) as { mapId?: unknown };
        const uploadedMapId = typeof parsed.mapId === 'string' ? parsed.mapId : null;
        await client.admin.importMapJson(rawJson);
        clearMapCache();
        if (uploadedMapId && uploadedMapId === gameState.getCurrentMapId()) {
          await refreshMap(uploadedMapId, { refreshRenderer: true, refreshNpcs: true });
        }
        return true;
      });
    },

    async saveFrontendMapAsset(): Promise<string> {
      return enqueueOperation(async () => {
        const currentMapId = gameState.getCurrentMapId();
        const definition = await fetchAuthoritativeMapDefinition(currentMapId);
        const refreshedMap = await refreshMap(currentMapId, { refreshRenderer: true });
        const document = await buildMapAssetDocument(definition, refreshedMap);
        return persistMapAssetDocument(document);
      });
    },

    async setMapDefaultBiome(mapId: string, biomeType: string): Promise<void> {
      await enqueueOperation(async () => {
        await client.admin.setMapDefaultBiome(mapId, biomeType);
        await refreshMap(mapId, { refreshRenderer: true });
      });
    },
  };
}
